import { makeStyles } from "@material-ui/core";
import { Linked } from "@type-r/models";
import { useIO } from "@type-r/react";
import { IServerSideDatasource } from "ag-grid-community";
import { AgGridColumnProps, AgGridReact } from "ag-grid-react";
import { isEqual } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AssetChange } from "../../api/assetChange";
import { AssetValidationCategory } from "../../api/assetValidationCategory";
import { CustomHeaderCheckbox } from "../invoices/custom-header-checkbox";

export enum Colors {
    Green = '#04d99d',
    Yellow = '#f2b824',
    Orange = '#f27324'
}

export enum AssetState {
    Draft = 'Draft',
    Digitized = 'Digitized'
}

export enum AssetStatus {
    PassValidationNewAsset = "Pass Validation - New",
    PassValidationModifiedAsset = "Pass Validation - Modified",
    RequireReview = "Require Review",
    Duplicated = "Duplicated",
    Conflict = "Conflict",
    SavedForLater = "Saved for Later",
    Digitized = "Digitized",
    ModifiesExistingAsset = "Modifies Existing"
}

export const getCellColor = (asset: AssetChange) => {
    if (!asset) {
        return
    }
    if (!asset.isPending && !asset.isDigitized) {
        const dynamic: any = asset;
        const validationCategory = dynamic.validationCategory;

        if (validationCategory == AssetValidationCategory.passesValidationNewAsset) {
            return Colors.Green
        }
        else if (validationCategory == AssetValidationCategory.passesValidationModifiedAsset) {
            return Colors.Green
        }
        else if (validationCategory == AssetValidationCategory.requiresReview) {
            return Colors.Orange
        }
        else if (validationCategory == AssetValidationCategory.duplicateRecordsWithinExistingAssets) {
            return Colors.Yellow
        }
        else if (validationCategory == AssetValidationCategory.duplicateRecordsWithinExistingFile) {
            return Colors.Yellow
        }
        else if (validationCategory == AssetValidationCategory.conflictsWithExistingFiles) {
            return Colors.Orange
        }
        else if (validationCategory == AssetValidationCategory.modifiesExistingAssets) {
            return Colors.Yellow
        }
    }
    else if (asset.isPending && !asset.isDigitized) {
        return Colors.Orange
    }
    else if (!asset.isPending && asset.isDigitized) {
        return Colors.Green
    }
}

export const getAssetStatus = (asset: AssetChange) => {
    if (!asset) {
        return
    }

    if (!asset.isPending && !asset.isDigitized) {
        const dynamic: any = asset;
        const validationCategory = dynamic.validationCategory;

        if (validationCategory == AssetValidationCategory.passesValidationNewAsset) {
            return AssetStatus.PassValidationNewAsset + ' ' + asset.getAssetName();
        }
        else if (validationCategory == AssetValidationCategory.passesValidationModifiedAsset) {
            return AssetStatus.PassValidationModifiedAsset + ' ' + asset.getAssetName();
        }
        else if (validationCategory == AssetValidationCategory.modifiesExistingAssets) {
            return AssetStatus.ModifiesExistingAsset + ' ' + asset.getAssetName();
        }
        else if (validationCategory == AssetValidationCategory.requiresReview) {
            return AssetStatus.RequireReview;
        }
        else if (validationCategory == AssetValidationCategory.duplicateRecordsWithinExistingAssets) {
            return AssetStatus.Duplicated;
        }
        else if (validationCategory == AssetValidationCategory.duplicateRecordsWithinExistingFile) {
            return AssetStatus.Duplicated;
        }
        else if (validationCategory == AssetValidationCategory.conflictsWithExistingFiles) {
            return AssetStatus.Conflict;
        }
    }
    else if (asset.isPending && !asset.isDigitized) {
        return AssetStatus.SavedForLater;
    }
    else if (!asset.isPending && asset.isDigitized) {
        return AssetStatus.Digitized;
    }
}

export const getAssetErrors = (asset: AssetChange) => {
    if (!asset) {
        return null;
    }
    const dynamic: any = asset;
    if (dynamic.errors && dynamic.errors.duplication) {
        const erroJson = JSON.parse(dynamic.errors.duplication);
        return erroJson.Message;
    } else if (dynamic.errors) {
        return Object.values(dynamic.errors).join(', ');
    } else {
        return null;
    }
};

export const AssetsTable = (
    {
        items,
        $active,
        $doDeleteAssetsJob,
        $filter,
        columns,
        jobId,
        querry,
        countModel,
        $doAssetUpdated,
        $countSelectedRows,
        $selectAllChecked,
        $selectedAssets,
        $isDeletedId
    }:
        {
            items: any,
            $active: Linked<any>,
            $doDeleteAssetsJob: Linked<boolean>,
            $filter: any,
            columns: AgGridColumnProps[],
            jobId: string,
            querry: any,
            countModel,
            $doAssetUpdated: Linked<boolean>,
            $countSelectedRows: Linked<number>,
            $selectAllChecked: Linked<boolean>,
            $selectedAssets: Linked<number[]>,
            $isDeletedId: Linked<any>
        }) => {
    const classes = useStyles();
    const [columnDefinition, _] = useState<AgGridColumnProps[]>(columns);
    const gridApi = useRef(null);

    const [selectAllIndeterminate, setSelectAllIndeterminate] = useState(false);
    const [unselectItems, setUnselectItems] = useState([]);
    const loadedItemCount = useRef(0);

    const onSelectionChanged = (event) => {
        const selectedRows = event.api!.getSelectedRows();
        if (selectedRows && selectedRows.length > 0) {
            if ($selectAllChecked.value && selectedRows.length !== loadedItemCount.current) {
                $selectAllChecked.value = false;
                $selectAllChecked.set(false);
                event.api.forEachNode((node) => {
                    if (!node.selected) {
                        unselectItems.push(node);
                        setUnselectItems(unselectItems);
                    }
                });
                $countSelectedRows.set(selectedRows.length);
            }
            if (!$selectAllChecked.value && selectedRows.length !== loadedItemCount.current) {
                setSelectAllIndeterminate(true);
                const selectedIds = [];
                selectedRows.map((row) => {
                    selectedIds.push(row.id);
                });
                $selectedAssets.set(selectedIds);
                $countSelectedRows.set(selectedRows.length);
            }
            if (selectedRows.length === loadedItemCount.current) {
                setSelectAllIndeterminate(false);
                $selectAllChecked.set(true);
                $countSelectedRows.set(countModel.total - countModel.digitized)
                setUnselectItems([]);
            }
        } else {
            setSelectAllIndeterminate(false);
            $selectAllChecked.set(false);
            setUnselectItems([]);
            $countSelectedRows.set(0);
        }
    };

    const onRemoveSelected = useCallback(() => {
        gridApi.current!.setServerSideDatasource(querry.getDataSource(jobId, (params) => items.fetch(params)));
        gridApi.current!.deselectAll()
    }, []);

    const onAssetUpdated = useIO(
        async () => $doAssetUpdated.isTruthy && await refreshGrid(),
        [$doAssetUpdated.value]
    );

    const refreshGrid = () => {
        if (gridApi.current) {
            gridApi.current.refreshCells();
        }
        $doAssetUpdated.set(false);
    }

    const createDatasource: (server: any) => IServerSideDatasource = _ => {
        return querry.getDataSource(jobId, (params) => items.fetch(params), countModel.total);
    }

    useEffect(() => {
        if (gridApi.current) {
            gridApi.current.refreshCells();
        }
    }, [items['_changeToken']]);

    useEffect(() => {
        if (gridApi.current) {
            onRemoveSelected();
        }
    }, [$doDeleteAssetsJob.value === true, $isDeletedId.value]);

    useMemo(() => {
        if (gridApi.current) {
            const filterInstance = gridApi.current.getFilterInstance("0");
            $filter.value ? filterInstance.setModel({ values: [$filter.value] }) : filterInstance.setModel(null);
            gridApi.current.onFilterChanged();
        }
    }, [$filter['_changeToken']]);

    const isRowSelectable = useMemo(() => {
        return (params) => !!params.data && params.data.isDigitized === false;
    }, []);

    const handlePaginationChanged = (_) => {
        if (gridApi.current) {
            let count = 0;
            gridApi.current.forEachNode((node, _) => {
                if (!node.data.isDigitized) {
                    count++;
                    loadedItemCount.current = count;
                }
                if ($selectAllChecked.value) {
                    if (unselectItems.find(x => isEqual(x, node)))
                        return;
                    node.setSelected($selectAllChecked.value);
                }
            });
        }
    }

    const handleSelectAll = (checked) => {
        checked ? $countSelectedRows.set(countModel.total - countModel.digitized) : $countSelectedRows.set(0)
        $selectAllChecked.set(checked);
        if (gridApi.current) {
            gridApi.current.forEachNode((node) => {
                node.setSelected(checked);
            });
        }
    }

    return (
        <div
            className={`ag-theme-balham ${classes.agCustomTheme}`}
            style={{ minHeight: '100%', width: '100%', flex: 1 }}
        >
            <AgGridReact
                serverSideDatasource={createDatasource({})}
                rowSelection={'multiple'}
                columnDefs={columnDefinition.map((colDef) => {
                    if (colDef.headerCheckboxSelection && !(countModel.digitized === countModel.total)) {
                        return {
                            ...colDef,
                            field: 'checkbox',
                            headerComponentFramework: CustomHeaderCheckbox,
                            headerComponentParams: {
                                indeterminate: selectAllIndeterminate,
                                checked: $selectAllChecked.value,
                                onSelectAll: handleSelectAll,
                            },
                        };
                    }
                    return colDef;
                })}
                pagination={true}
                defaultColDef={{
                    filter: true,
                    resizable: true
                }}
                rowModelType={'serverSide'}
                suppressRowClickSelection={true}
                onSelectionChanged={onSelectionChanged}
                onFilterChanged={() => gridApi.current!.deselectAll()}
                onGridReady={params => gridApi.current = params.api}
                onFirstDataRendered={params => { params.columnApi.autoSizeAllColumns(); }}
                onPaginationChanged={handlePaginationChanged}
                isRowSelectable={isRowSelectable}
                paginationPageSize={100}
                onRowClicked={(event) => $active.set(event.data)}
            />
        </div>
    )
}

const useStyles = makeStyles({
    agCustomTheme: {
        '& .ag-header': {
            background: 'white',
            borderBottom: '1px solid #bdc3c7',
            fontSize: 13,
            color: '#052C4C',
        },
        '& .ag-row-selected': {
            background: '#ccdbe7 !important',
            borderColor: '#bdc3c7'
        },
        '& .ag-row-hover': {
            background: '#052C4C1a'
        }
    }
})
